import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Plus, 
  Trash2, 
  Eye,
  EyeOff,
  Copy,
  CheckCircle,
  Circle,
  FileText,
  Code,
  BookOpen,
  Edit,
  Globe,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useClientApiKeys } from '@/hooks/useClientApiKeys';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface DefaultApiKeysSectionProps {
  isEditing: boolean;
}

interface ApiDocumentation {
  description: string;
  baseUrl?: string;
  authMethod?: string;
  commonEndpoints?: { name: string; method: string; endpoint: string; description: string }[];
  sampleCalls?: { name: string; description: string; code: string }[];
}

interface StoredApiDocumentation {
  id: string;
  service_name: string;
  source_url: string;
  documentation: any;
  scraped_at: string;
  status: string;
}

const serviceDocumentation: Record<string, ApiDocumentation> = {
  'b2b_enrichment': {
    description: 'B2B Enrichment API for LinkedIn profile data, people search, and email finding',
    baseUrl: 'https://app.b2b-enrichment.com/api/b2b-enrichment',
    authMethod: 'X-TOKEN header',
    commonEndpoints: [
      { name: 'Fetch Profile V2', method: 'POST', endpoint: '/v2/profiles', description: 'Enhanced LinkedIn profile data' },
      { name: 'People Search V2', method: 'POST', endpoint: '/v2/people', description: 'Search across 400+ million profiles' },
      { name: 'Email Finding', method: 'POST', endpoint: '/email-management/findings', description: 'Find email addresses' },
      { name: 'Email Verification', method: 'POST', endpoint: '/email-management/verifications', description: 'Verify email validity' },
      { name: 'Check Credits', method: 'GET', endpoint: '/credits', description: 'Get current credit balance' }
    ],
    sampleCalls: [
      {
        name: 'Fetch LinkedIn Profile',
        description: 'Get detailed profile data from LinkedIn URL',
        code: `curl https://app.b2b-enrichment.com/api/b2b-enrichment/v2/profiles \\
  --request POST \\
  --header 'Content-Type: application/json' \\
  --header 'X-TOKEN: YOUR_SECRET_TOKEN' \\
  --data '{
    "url": "https://www.linkedin.com/in/username",
    "useCache": true
  }'`
      },
      {
        name: 'People Search',
        description: 'Search for people by company and job title',
        code: `curl https://app.b2b-enrichment.com/api/b2b-enrichment/v2/people \\
  --request POST \\
  --header 'Content-Type: application/json' \\
  --header 'X-TOKEN: YOUR_SECRET_TOKEN' \\
  --data '{
    "account": {
      "domain": ["google.com"],
      "industry": ["software development"]
    },
    "contact": {
      "jobTitle": ["founder"],
      "location": ["united states"]
    },
    "size": 100,
    "page": 0
  }'`
      }
    ]
  },
  'openai': {
    description: 'OpenAI API for GPT models, embeddings, and other AI services',
    baseUrl: 'https://api.openai.com/v1',
    authMethod: 'Bearer token in Authorization header',
    commonEndpoints: [
      { name: 'Chat Completions', method: 'POST', endpoint: '/chat/completions', description: 'Generate chat responses' },
      { name: 'Embeddings', method: 'POST', endpoint: '/embeddings', description: 'Create text embeddings' },
      { name: 'Fine-tuning Jobs', method: 'POST', endpoint: '/fine_tuning/jobs', description: 'Create fine-tuning job' }
    ]
  },
  'perplexity': {
    description: 'Perplexity API for real-time web search and reasoning',
    baseUrl: 'https://api.perplexity.ai',
    authMethod: 'Bearer token in Authorization header'
  }
};

const commonServices = [
  { value: 'openai', label: 'OpenAI', description: 'GPT models, embeddings, and other AI services' },
  { value: 'perplexity', label: 'Perplexity', description: 'Real-time web search and reasoning' },
  { value: 'together_ai', label: 'Together.ai', description: 'Open-source model inference' },
  { value: 'serper', label: 'Serper.dev', description: 'Google search API and web scraping' },
  { value: 'anthropic', label: 'Anthropic', description: 'Claude AI models' },
  { value: 'cohere', label: 'Cohere', description: 'Language AI platform' },
  { value: 'huggingface', label: 'Hugging Face', description: 'ML models and inference' },
  { value: 'replicate', label: 'Replicate', description: 'Run ML models in the cloud' },
  { value: 'b2b_enrichment', label: 'B2B Enrichment', description: 'LinkedIn data and email finding' },
];

export function DefaultApiKeysSection({ isEditing }: DefaultApiKeysSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingService, setEditingService] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    api_key: '',
    secret_key: '',
    api_docs_url: '',
    notes: ''
  });
  const [showApiKey, setShowApiKey] = useState<Record<string, boolean>>({});
  const [customService, setCustomService] = useState('');
  const [selectedDocumentation, setSelectedDocumentation] = useState<string | null>(null);
  const [showApiDocsForm, setShowApiDocsForm] = useState(false);
  const [storedDocs, setStoredDocs] = useState<StoredApiDocumentation[]>([]);
  const [selectedStoredDoc, setSelectedStoredDoc] = useState<StoredApiDocumentation | null>(null);
  const [isScrapingDocs, setIsScrapingDocs] = useState(false);
  const [scrapingForm, setScrapingForm] = useState({
    url: '',
    serviceName: ''
  });
  const [newKey, setNewKey] = useState({
    service_name: '',
    api_key: '',
    secret_key: '',
    documentation_url: '',
    notes: '',
    use_as_default: true,
    client_id: 'default'
  });
  const [serviceInputType, setServiceInputType] = useState<'existing' | 'new'>('new');
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClientForDisplay, setSelectedClientForDisplay] = useState<string>('default');

  // Use selectedClientForDisplay to fetch keys for the selected client
  const { apiKeys, loading, createApiKey, deleteApiKey, activateApiKey, updateApiKey } = useClientApiKeys(selectedClientForDisplay);
  const { toast } = useToast();

  // Fetch clients and stored API documentation
  useEffect(() => {
    fetchClients();
    fetchStoredDocs();
  }, []);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .neq('id', 'default') // Exclude the system default client from dropdown
        .order('name');

      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchStoredDocs = async () => {
    try {
      const { data, error } = await supabase
        .from('api_documentation')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setStoredDocs(data || []);
    } catch (error) {
      console.error('Error fetching API docs:', error);
    }
  };

  const handleScrapeApiDocs = async () => {
    if (!scrapingForm.url || !scrapingForm.serviceName) {
      toast({
        title: "Missing information",
        description: "Please enter both URL and service name",
        variant: "destructive"
      });
      return;
    }

    setIsScrapingDocs(true);
    try {
      const { data, error } = await supabase.functions.invoke('scrape-api-docs', {
        body: { 
          url: scrapingForm.url, 
          serviceName: scrapingForm.serviceName 
        }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: "Documentation scraped!",
          description: `Successfully scraped documentation for ${scrapingForm.serviceName}`,
        });
        setScrapingForm({ url: '', serviceName: '' });
        setShowApiDocsForm(false);
        fetchStoredDocs();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error scraping API docs:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to scrape API documentation",
        variant: "destructive"
      });
    } finally {
      setIsScrapingDocs(false);
    }
  };

  const handleAddKey = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newKey.service_name || !newKey.api_key) {
      toast({
        title: "Error",
        description: "Please provide both service name and API key.",
        variant: "destructive",
      });
      return;
    }

    try {
      await createApiKey({
        service_name: newKey.service_name as any,
        api_key: newKey.api_key,
        secret_key: newKey.secret_key || undefined,
        notes: newKey.notes,
        client_id: newKey.client_id,
        created_by: 'admin'
      });

      toast({
        title: "Success",
        description: `API key for ${newKey.service_name} added successfully.`,
      });

      setNewKey({ 
        service_name: '', 
        api_key: '', 
        secret_key: '',
        documentation_url: '', 
        notes: '', 
        use_as_default: true, 
        client_id: 'default' 
      });
      setShowAddForm(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add API key.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteKey = async (keyId: string) => {
    try {
      await deleteApiKey(keyId);
      toast({
        title: "Success",
        description: "Default API key deleted successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete API key.",
        variant: "destructive",
      });
    }
  };

  const handleActivateKey = async (keyId: string, serviceName: string) => {
    try {
      await activateApiKey(keyId, selectedClientForDisplay, serviceName as any);
      toast({
        title: "Success",
        description: "Default API key activated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to activate API key.",
        variant: "destructive",
      });
    }
  };

  const handleEditSave = async () => {
    try {
      const serviceKeys = keysByService[editingService || ''] || [];
      const activeKey = serviceKeys.find(key => key.is_active);
      
      if (activeKey) {
        // Update existing key
        await updateApiKey(activeKey.id, {
          api_key: editForm.api_key,
          secret_key: editForm.secret_key || undefined,
          notes: editForm.notes
        });
      } else {
        // Create new key for this service
        await createApiKey({
          service_name: editingService as any,
          api_key: editForm.api_key,
          secret_key: editForm.secret_key || undefined,
          notes: editForm.notes,
          client_id: selectedClientForDisplay,
          created_by: 'admin'
        });
      }

      setEditingService(null);
      setEditForm({ api_key: '', secret_key: '', api_docs_url: '', notes: '' });
    } catch (error) {
      console.error('Error saving API key:', error);
    }
  };

  const toggleApiKeyVisibility = (keyId: string) => {
    setShowApiKey(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: text.length > 50 ? "Content copied to clipboard" : "API key copied to clipboard",
    });
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return '***';
    return key.substring(0, 4) + '***' + key.substring(key.length - 4);
  };

  // Group keys by service
  const keysByService = apiKeys.reduce((acc, key) => {
    if (!acc[key.service_name]) {
      acc[key.service_name] = [];
    }
    acc[key.service_name].push(key);
    return acc;
  }, {} as Record<string, typeof apiKeys>);

  // Get all unique services (both from common list and existing keys)
  const allServices = [...commonServices];
  Object.keys(keysByService).forEach(service => {
    if (!commonServices.find(s => s.value === service)) {
      allServices.push({
        value: service,
        label: service.charAt(0).toUpperCase() + service.slice(1),
        description: 'Custom service'
      });
    }
  });

  return (
    <div className="space-y-4">
      {/* Client Selection for Viewing Keys */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">API Keys Management</h3>
          <p className="text-sm text-muted-foreground">
            Manage API keys for different clients and services.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="client_selector" className="text-sm font-medium">View keys for:</Label>
            <Select 
              value={selectedClientForDisplay} 
              onValueChange={setSelectedClientForDisplay}
            >
              <SelectTrigger className="w-48">
                <SelectValue>
                  {selectedClientForDisplay === 'default' 
                    ? 'Global Default Keys' 
                    : clients.find(c => c.id === selectedClientForDisplay)?.name || 'Select client'
                  }
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">
                  <div className="flex flex-col">
                    <span>Global Keys</span>
                    <span className="text-xs text-muted-foreground">Shared across all clients</span>
                  </div>
                </SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    <div className="flex flex-col">
                      <span>{client.name}</span>
                      <span className="text-xs text-muted-foreground">Client-specific keys</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
           {isEditing && (
             <div className="flex gap-2">
               <Button onClick={() => setShowAddForm(true)} className="flex items-center gap-2">
                 <Plus className="h-4 w-4" />
                 Add API Key
               </Button>
               <Button 
                 variant="outline" 
                 onClick={() => setShowApiDocsForm(true)} 
                 className="flex items-center gap-2"
               >
                 <BookOpen className="h-4 w-4" />
                 API Docs
               </Button>
             </div>
           )}
         </div>
       </div>

      {/* API Documentation Form */}
      {showApiDocsForm && isEditing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Scrape API Documentation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left side - Scraping form */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="service-name">Service Name</Label>
                  <Input
                    id="service-name"
                    value={scrapingForm.serviceName}
                    onChange={(e) => setScrapingForm(prev => ({ ...prev, serviceName: e.target.value }))}
                    placeholder="e.g., Stripe, OpenAI, Twilio"
                  />
                </div>
                
                <div>
                  <Label htmlFor="docs-url">Documentation URL</Label>
                  <Input
                    id="docs-url"
                    value={scrapingForm.url}
                    onChange={(e) => setScrapingForm(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="https://docs.example.com/api"
                  />
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={handleScrapeApiDocs} 
                    disabled={isScrapingDocs} 
                    className="flex-1"
                  >
                    {isScrapingDocs ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scraping...
                      </>
                    ) : (
                      <>
                        <Globe className="w-4 h-4 mr-2" />
                        Scrape Documentation
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowApiDocsForm(false)}
                  >
                    Cancel
                  </Button>
                </div>

                <div className="text-sm text-muted-foreground p-3 bg-muted/50 rounded-lg">
                  <p>This will use Perplexity AI to intelligently scrape and process the API documentation from the provided URL.</p>
                </div>
              </div>

              {/* Right side - Stored docs */}
              <div className="space-y-4">
                <h4 className="font-medium">Stored Documentation</h4>
                <ScrollArea className="h-64 border rounded-lg p-2">
                  <div className="space-y-2">
                    {storedDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          selectedStoredDoc?.id === doc.id ? 'bg-primary/10 border-primary' : 'hover:bg-muted'
                        }`}
                        onClick={() => setSelectedStoredDoc(doc)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-sm">{doc.service_name}</h4>
                          {(() => {
                            switch (doc.status) {
                              case 'completed':
                                return <Badge variant="default" className="bg-green-100 text-green-800 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />Done</Badge>;
                              case 'pending':
                                return <Badge variant="secondary" className="text-xs"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
                              case 'error':
                                return <Badge variant="destructive" className="text-xs"><AlertCircle className="w-3 h-3 mr-1" />Error</Badge>;
                              default:
                                return <Badge variant="outline" className="text-xs">{doc.status}</Badge>;
                            }
                          })()}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {doc.source_url}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(doc.scraped_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                    {storedDocs.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No documentation stored yet</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
                
                {selectedStoredDoc && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">{selectedStoredDoc.service_name} Preview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-xs text-muted-foreground mb-2">
                        <p>Source: <a href={selectedStoredDoc.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedStoredDoc.source_url}</a></p>
                      </div>
                      <ScrollArea className="h-32 bg-muted p-2 rounded text-xs">
                        {typeof selectedStoredDoc.documentation === 'string' 
                          ? selectedStoredDoc.documentation.substring(0, 300) + '...'
                          : JSON.stringify(selectedStoredDoc.documentation, null, 2).substring(0, 300) + '...'
                        }
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Key Form */}
      {showAddForm && isEditing && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add New API Key</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddKey} className="space-y-4">
              <div>
                <Label>Service Name</Label>
                <RadioGroup
                  value={serviceInputType}
                  onValueChange={(value: 'existing' | 'new') => {
                    setServiceInputType(value);
                    setNewKey(prev => ({ ...prev, service_name: '' }));
                  }}
                  className="flex gap-6 mb-3"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="existing" id="existing" />
                    <Label htmlFor="existing">Existing Service</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="new" id="new" />
                    <Label htmlFor="new">New Service</Label>
                  </div>
                </RadioGroup>
                
                {serviceInputType === 'existing' ? (
                  <Select 
                    value={newKey.service_name} 
                    onValueChange={(value) => setNewKey(prev => ({ ...prev, service_name: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-background border z-50">
                      {commonServices.map((service) => (
                        <SelectItem key={service.value} value={service.value}>
                          <div className="flex flex-col">
                            <span>{service.label}</span>
                            <span className="text-xs text-muted-foreground">{service.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Enter custom service name (e.g., Stripe, Twilio, Custom API)"
                    value={newKey.service_name}
                    onChange={(e) => setNewKey(prev => ({ ...prev, service_name: e.target.value }))}
                  />
                )}
              </div>
              
              <div>
                <Label htmlFor="client_id">Client</Label>
                <Select 
                  value={newKey.client_id} 
                  onValueChange={(value) => setNewKey(prev => ({ ...prev, client_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select client or default" />
                  </SelectTrigger>
                  <SelectContent className="bg-background border z-50">
                    <SelectItem value="default">
                      Global Default Keys
                    </SelectItem>
                    {clients.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="api_key">API Key</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={newKey.api_key}
                  onChange={(e) => setNewKey(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter your API key"
                />
              </div>
              
              <div>
                <Label htmlFor="secret_key">Secret Key (Optional)</Label>
                <Input
                  id="secret_key"
                  type="password"
                  value={newKey.secret_key}
                  onChange={(e) => setNewKey(prev => ({ ...prev, secret_key: e.target.value }))}
                  placeholder="Enter your secret key (if applicable)"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Some APIs require both public and secret keys (e.g., Stripe)
                </p>
              </div>
              
              <div>
                <Label htmlFor="docs_url">Documentation URL</Label>
                <Input
                  id="docs_url"
                  value={newKey.documentation_url}
                  onChange={(e) => setNewKey(prev => ({ ...prev, documentation_url: e.target.value }))}
                  placeholder="https://docs.example.com/api"
                />
              </div>
              
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={newKey.notes}
                  onChange={(e) => setNewKey(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes about this API key"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="use_as_default"
                  checked={newKey.use_as_default}
                  onCheckedChange={(checked) => setNewKey(prev => ({ ...prev, use_as_default: checked }))}
                />
                <Label htmlFor="use_as_default">Use as default</Label>
              </div>
              
              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  Add API Key
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* API Keys List */}
      <ScrollArea className="h-[600px]">
        <div className="space-y-2">
          {allServices.map((service) => {
            const serviceKeys = keysByService[service.value] || [];
            const activeKey = serviceKeys.find(key => key.is_active);
            const documentation = serviceDocumentation[service.value];
            
            return (
              <Card key={service.value} className="relative cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
                setEditingService(service.value);
                const activeKey = serviceKeys.find(key => key.is_active);
                if (activeKey) {
                  // Populate form with existing key data for editing
                  setEditForm({
                    api_key: activeKey.api_key,
                    secret_key: activeKey.secret_key || '',
                    api_docs_url: '',
                    notes: activeKey.notes || ''
                  });
                } else {
                  // Reset form for adding new key
                  setEditForm({
                    api_key: '',
                    secret_key: '',
                    api_docs_url: '',
                    notes: ''
                  });
                }
              }}>
                <CardHeader className="pb-1 pt-2 px-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-base flex items-center gap-2">
                        {service.label}
                        {activeKey && (
                          <Badge variant="default" className="text-xs">
                            Default Active
                          </Badge>
                        )}
                        {!activeKey && (
                          <Badge variant="secondary" className="text-xs">
                            No Default Key
                          </Badge>
                        )}
                        {documentation && (
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                <FileText className="h-3 w-3" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                              <DialogHeader>
                                <DialogTitle>{service.label} API Documentation</DialogTitle>
                              </DialogHeader>
                              <Tabs defaultValue="overview" className="w-full">
                                <TabsList className="grid w-full grid-cols-3">
                                  <TabsTrigger value="overview">Overview</TabsTrigger>
                                  <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
                                  <TabsTrigger value="examples">Examples</TabsTrigger>
                                </TabsList>
                                <TabsContent value="overview" className="space-y-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Card>
                                      <CardHeader>
                                        <CardTitle className="text-sm">Description</CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <p className="text-sm">{documentation.description}</p>
                                      </CardContent>
                                    </Card>
                                    <Card>
                                      <CardHeader>
                                        <CardTitle className="text-sm">Authentication</CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <p className="text-sm">{documentation.authMethod}</p>
                                        {documentation.baseUrl && (
                                          <p className="text-xs text-muted-foreground mt-2">
                                            Base URL: {documentation.baseUrl}
                                          </p>
                                        )}
                                      </CardContent>
                                    </Card>
                                  </div>
                                </TabsContent>
                                <TabsContent value="endpoints" className="space-y-4">
                                  {documentation.commonEndpoints && (
                                    <div className="space-y-2">
                                      {documentation.commonEndpoints.map((endpoint, index) => (
                                        <Card key={index}>
                                          <CardContent className="pt-4">
                                            <div className="flex items-center gap-2 mb-2">
                                              <Badge variant="outline" className="text-xs">
                                                {endpoint.method}
                                              </Badge>
                                              <code className="text-sm">{endpoint.endpoint}</code>
                                            </div>
                                            <h4 className="font-medium text-sm">{endpoint.name}</h4>
                                            <p className="text-xs text-muted-foreground">{endpoint.description}</p>
                                          </CardContent>
                                        </Card>
                                      ))}
                                    </div>
                                  )}
                                </TabsContent>
                                <TabsContent value="examples" className="space-y-4">
                                  {documentation.sampleCalls && (
                                    <div className="space-y-4">
                                      {documentation.sampleCalls.map((example, index) => (
                                        <Card key={index}>
                                          <CardHeader>
                                            <div className="flex items-center justify-between">
                                              <CardTitle className="text-sm">{example.name}</CardTitle>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => copyToClipboard(example.code)}
                                              >
                                                <Copy className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </CardHeader>
                                          <CardContent>
                                            <p className="text-xs text-muted-foreground mb-2">{example.description}</p>
                                            <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-xs overflow-x-auto">
                                              <code>{example.code}</code>
                                            </pre>
                                          </CardContent>
                                        </Card>
                                      ))}
                                    </div>
                                  )}
                                </TabsContent>
                              </Tabs>
                            </DialogContent>
                          </Dialog>
                        )}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground leading-tight">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 pb-2 px-4">
                  {serviceKeys.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">
                      No default API key configured.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {serviceKeys.map((key) => (
                        <div key={key.id} className="flex items-center justify-between p-2 border rounded-lg bg-muted/30">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs truncate">
                                {showApiKey[key.id] ? key.api_key : maskApiKey(key.api_key)}
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => toggleApiKeyVisibility(key.id)}
                              >
                                {showApiKey[key.id] ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => copyToClipboard(key.api_key)}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                            {key.notes && (
                              <p className="text-xs text-muted-foreground truncate">{key.notes}</p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Added {new Date(key.created_at).toLocaleDateString()}
                              {key.created_by && ` by ${key.created_by}`}
                            </p>
                          </div>
                          {isEditing && (
                            <div className="flex items-center gap-1 ml-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => handleActivateKey(key.id, key.service_name)}
                                disabled={key.is_active}
                              >
                                {key.is_active ? (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Circle className="h-4 w-4" />
                                )}
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-600 hover:text-red-700">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Default API Key</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete this {service.label} default API key? This action cannot be undone and will affect all clients using this fallback key.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteKey(key.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      {/* Service Edit Dialog */}
      {editingService && (
        <Dialog open={!!editingService} onOpenChange={() => setEditingService(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit {allServices.find(s => s.value === editingService)?.label} Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="service_api_key">API Key</Label>
                <Input
                  id="service_api_key"
                  type="password"
                  value={editForm.api_key}
                  onChange={(e) => setEditForm(prev => ({ ...prev, api_key: e.target.value }))}
                  placeholder="Enter API key"
                />
              </div>
              
              <div>
                <Label htmlFor="service_secret_key">Secret Key (Optional)</Label>
                <Input
                  id="service_secret_key"
                  type="password"
                  value={editForm.secret_key}
                  onChange={(e) => setEditForm(prev => ({ ...prev, secret_key: e.target.value }))}
                  placeholder="Enter secret key (if applicable)"
                />
              </div>
              
              <div>
                <Label htmlFor="service_api_docs_url">API Docs URL (Optional)</Label>
                <Input
                  id="service_api_docs_url"
                  value={editForm.api_docs_url}
                  onChange={(e) => setEditForm(prev => ({ ...prev, api_docs_url: e.target.value }))}
                  placeholder="https://docs.example.com/api"
                />
                <div className="flex items-center gap-2 mt-2">
                  {storedDocs.find(doc => doc.service_name.toLowerCase() === editingService) ? (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle2 className="h-3 w-3" />
                      Documentation scraped
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      Not scraped yet
                    </div>
                  )}
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setScrapingForm({ 
                        url: editForm.api_docs_url, 
                        serviceName: allServices.find(s => s.value === editingService)?.label || editingService || ''
                      });
                      setShowApiDocsForm(true);
                    }}
                    disabled={!editForm.api_docs_url}
                  >
                    <Globe className="h-3 w-3 mr-1" />
                    Scrape Docs
                  </Button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="service_notes">Notes</Label>
                <Textarea
                  id="service_notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional notes about this API key"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleEditSave} disabled={loading}>
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditingService(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}